import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';
import { useAuth } from '../../../modules/shared/context/AuthContext';

// Criar o contexto
const ObrasFixasContext = createContext(null);

// Hook personalizado para usar o contexto
export const useObrasFixas = () => {
  const context = useContext(ObrasFixasContext);
  if (!context) {
    throw new Error('useObrasFixas deve ser usado dentro de um ObrasFixasProvider');
  }
  return context;
};

// Componente Provider
export const ObrasFixasProvider = ({ children }) => {
  const [obrasFixas, setObrasFixas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, checkAndRefreshToken } = useAuth();

  // Carregar obras fixas do Supabase quando o usuário estiver autenticado
  useEffect(() => {
    const fetchObrasFixas = async () => {
      if (!user) {
        setObrasFixas([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Verificar e renovar token se necessário
        await checkAndRefreshToken();
        
        // Buscar todas as obras fixas
        const { data: obrasFixasData, error: obrasFixasError } = await supabase
          .from('obras_fixas')
          .select('*');
        
        if (obrasFixasError) throw obrasFixasError;
        
        // Para cada obra fixa, buscar seus itens
        const obrasFixasCompletas = await Promise.all(obrasFixasData.map(async (obraFixa) => {
          // Buscar itens da obra fixa
          const { data: itensData } = await supabase
            .from('itens')
            .select('*')
            .eq('obra_fixa_id', obraFixa.id);
          
          // Buscar detalhes para cada item
          const itensComDetalhes = await Promise.all((itensData || []).map(async (item) => {
            const { data: detalhesData } = await supabase
              .from('detalhes')
              .select('*')
              .eq('item_id', item.id);
            
            // Mapear campos para o formato esperado pela aplicação
            return {
              ...item,
              quantidadeMedida: item.quantidade_medida,
              totalMedido: item.total_medido,
              detalhes: detalhesData || []
            };
          }));
          
          // Retornar obra fixa completa com itens, convertendo snake_case para camelCase
          return {
            id: obraFixa.id,
            nome: obraFixa.nome,
            empreiteiro: obraFixa.empreiteiro,
            valor: obraFixa.valor,
            status: obraFixa.status,
            dataInicio: obraFixa.data_inicio,
            dataPrevisaoTermino: obraFixa.data_previsao_termino,
            observacoes: obraFixa.observacoes,
            contrato: obraFixa.contrato,
            licitacao: obraFixa.licitacao,
            processo: obraFixa.processo,
            gestor: obraFixa.gestor,
            fiscaisObras: obraFixa.fiscais_obras,
            totalMedicoes: obraFixa.total_medicoes,
            // Campos específicos para obras fixas
            localizacao: {
              latitude: obraFixa.latitude,
              longitude: obraFixa.longitude,
              nome: obraFixa.nome_local,
              descricao: obraFixa.descricao_local
            },
            itens: itensComDetalhes || []
          };
        }));
        
        setObrasFixas(obrasFixasCompletas);
        setError(null);
      } catch (error) {
        console.error('Erro ao carregar obras fixas:', error);
        setError(error.message);
        toast.error('Erro ao carregar dados das obras fixas');
      } finally {
        setLoading(false);
      }
    };

    fetchObrasFixas();
  }, [user, checkAndRefreshToken]);

  // Adicionar uma nova obra fixa
  const adicionarObraFixa = async (novaObraFixa) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return null;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Extrair itens para inserir separadamente
      const { itens = [], localizacao = {}, ...dadosObraFixa } = novaObraFixa;
      
      // Inserir a obra fixa principal
      const { data: obraFixaInserida, error: obraFixaError } = await supabase
        .from('obras_fixas')
        .insert([{
          nome: dadosObraFixa.nome,
          empreiteiro: dadosObraFixa.empreiteiro,
          valor: parseFloat(dadosObraFixa.valor),
          status: dadosObraFixa.status,
          data_inicio: dadosObraFixa.dataInicio,
          data_previsao_termino: dadosObraFixa.dataPrevisaoTermino,
          observacoes: dadosObraFixa.observacoes,
          contrato: dadosObraFixa.contrato,
          licitacao: dadosObraFixa.licitacao,
          processo: dadosObraFixa.processo,
          gestor: dadosObraFixa.gestor,
          fiscais_obras: dadosObraFixa.fiscaisObras,
          total_medicoes: parseFloat(dadosObraFixa.totalMedicoes || 0),
          // Campos específicos para obras fixas
          latitude: localizacao.latitude,
          longitude: localizacao.longitude,
          nome_local: localizacao.nome,
          descricao_local: localizacao.descricao,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select();
      
      if (obraFixaError) throw obraFixaError;
      
      const obraFixaId = obraFixaInserida[0].id;
      let itensInseridos = [];
      
      // Inserir itens
      if (itens && itens.length > 0) {
        for (const item of itens) {
          const { detalhes = [], ...dadosItem } = item;
          
          // Inserir item
          const { data: itemInserido, error: itemError } = await supabase
            .from('itens')
            .insert([{ 
              item: dadosItem.item,
              descricao: dadosItem.descricao,
              unidade: dadosItem.unidade,
              obra_fixa_id: obraFixaId,
              preco: parseFloat(dadosItem.preco),
              quantidade: parseFloat(dadosItem.quantidade),
              total: parseFloat(dadosItem.total),
              quantidade_medida: parseFloat(dadosItem.quantidadeMedida || 0),
              total_medido: parseFloat(dadosItem.totalMedido || 0),
              progresso: parseFloat(dadosItem.progresso || 0),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (itemError) throw itemError;
          
          const itemId = itemInserido[0].id;
          let detalhesInseridos = [];
          
          // Inserir detalhes do item
          if (detalhes && detalhes.length > 0) {
            for (const detalhe of detalhes) {
              const { data: detalheInserido, error: detalheError } = await supabase
                .from('detalhes')
                .insert([{ 
                  data: detalhe.data,
                  operacao: detalhe.operacao,
                  numero_medicao: detalhe.numeroMedicao,
                  descricao_medicao: detalhe.descricaoMedicao,
                  item_id: itemId,
                  quantidade: parseFloat(detalhe.quantidade),
                  valor: parseFloat(detalhe.valor),
                  total: parseFloat(detalhe.total),
                  saldo_quantidade: parseFloat(detalhe.saldoQuantidade || 0),
                  saldo_valor: parseFloat(detalhe.saldoValor || 0),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }])
                .select();
              
              if (detalheError) throw detalheError;
              detalhesInseridos.push(detalheInserido[0]);
            }
          }
          
          itensInseridos.push({
            ...itemInserido[0],
            detalhes: detalhesInseridos
          });
        }
      }
      
      // Construir a obra fixa completa com os dados inseridos, convertendo snake_case para camelCase
      const obraFixaCompleta = {
        id: obraFixaInserida[0].id,
        nome: obraFixaInserida[0].nome,
        empreiteiro: obraFixaInserida[0].empreiteiro,
        valor: obraFixaInserida[0].valor,
        status: obraFixaInserida[0].status,
        dataInicio: obraFixaInserida[0].data_inicio,
        dataPrevisaoTermino: obraFixaInserida[0].data_previsao_termino,
        observacoes: obraFixaInserida[0].observacoes,
        contrato: obraFixaInserida[0].contrato,
        licitacao: obraFixaInserida[0].licitacao,
        processo: obraFixaInserida[0].processo,
        gestor: obraFixaInserida[0].gestor,
        fiscaisObras: obraFixaInserida[0].fiscais_obras,
        totalMedicoes: obraFixaInserida[0].total_medicoes,
        localizacao: {
          latitude: obraFixaInserida[0].latitude,
          longitude: obraFixaInserida[0].longitude,
          nome: obraFixaInserida[0].nome_local,
          descricao: obraFixaInserida[0].descricao_local
        },
        itens: itensInseridos
      };
      
      // Atualizar o estado local
      setObrasFixas(prevObrasFixas => [...prevObrasFixas, obraFixaCompleta]);
      
      toast.success('Obra fixa cadastrada com sucesso!');
      return obraFixaCompleta;
    } catch (error) {
      console.error('Erro ao cadastrar obra fixa:', error);
      toast.error('Erro ao cadastrar obra fixa: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar uma obra fixa existente
  const atualizarObraFixa = async (id, dadosAtualizados) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return false;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Extrair itens para atualizar separadamente
      const { itens = [], localizacao = {}, ...dadosObraFixa } = dadosAtualizados;
      
      // Atualizar a obra fixa principal
      const { error: obraFixaError } = await supabase
        .from('obras_fixas')
        .update({
          nome: dadosObraFixa.nome,
          empreiteiro: dadosObraFixa.empreiteiro,
          valor: parseFloat(dadosObraFixa.valor),
          status: dadosObraFixa.status,
          data_inicio: dadosObraFixa.dataInicio,
          data_previsao_termino: dadosObraFixa.dataPrevisaoTermino,
          observacoes: dadosObraFixa.observacoes,
          contrato: dadosObraFixa.contrato,
          licitacao: dadosObraFixa.licitacao,
          processo: dadosObraFixa.processo,
          gestor: dadosObraFixa.gestor,
          fiscais_obras: dadosObraFixa.fiscaisObras,
          total_medicoes: parseFloat(dadosObraFixa.totalMedicoes || 0),
          // Campos específicos para obras fixas
          latitude: localizacao.latitude,
          longitude: localizacao.longitude,
          nome_local: localizacao.nome,
          descricao_local: localizacao.descricao,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (obraFixaError) throw obraFixaError;
      
      // Atualizar itens
      if (itens && itens.length > 0) {
        // Primeiro, excluir todos os itens existentes (e seus detalhes em cascata)
        const { error: deleteItemsError } = await supabase
          .from('itens')
          .delete()
          .eq('obra_fixa_id', id);
        
        if (deleteItemsError) throw deleteItemsError;
        
        // Inserir os novos itens
        for (const item of itens) {
          const { detalhes = [], ...dadosItem } = item;
          
          // Inserir item
          const { data: itemInserido, error: itemError } = await supabase
            .from('itens')
            .insert([{ 
              item: dadosItem.item,
              descricao: dadosItem.descricao,
              unidade: dadosItem.unidade,
              obra_fixa_id: id,
              preco: parseFloat(dadosItem.preco),
              quantidade: parseFloat(dadosItem.quantidade),
              total: parseFloat(dadosItem.total),
              quantidade_medida: parseFloat(dadosItem.quantidadeMedida || 0),
              total_medido: parseFloat(dadosItem.totalMedido || 0),
              progresso: parseFloat(dadosItem.progresso || 0),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (itemError) throw itemError;
          
          const itemId = itemInserido[0].id;
          
          // Inserir detalhes do item
          if (detalhes && detalhes.length > 0) {
            for (const detalhe of detalhes) {
              const { error: detalheError } = await supabase
                .from('detalhes')
                .insert([{ 
                  data: detalhe.data,
                  operacao: detalhe.operacao,
                  numero_medicao: detalhe.numeroMedicao,
                  descricao_medicao: detalhe.descricaoMedicao,
                  item_id: itemId,
                  quantidade: parseFloat(detalhe.quantidade),
                  valor: parseFloat(detalhe.valor),
                  total: parseFloat(detalhe.total),
                  saldo_quantidade: parseFloat(detalhe.saldoQuantidade || 0),
                  saldo_valor: parseFloat(detalhe.saldoValor || 0),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }]);
              
              if (detalheError) throw detalheError;
            }
          }
        }
      }
      
      // Buscar a obra fixa atualizada completa
      const { data: obraFixaAtualizada, error: fetchError } = await supabase
        .from('obras_fixas')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Buscar itens da obra fixa
      const { data: itensData } = await supabase
        .from('itens')
        .select('*')
        .eq('obra_fixa_id', id);
      
      // Buscar detalhes para cada item
      const itensComDetalhes = await Promise.all((itensData || []).map(async (item) => {
        const { data: detalhesData } = await supabase
          .from('detalhes')
          .select('*')
          .eq('item_id', item.id);
        
        // Mapear campos para o formato esperado pela aplicação
        return {
          ...item,
          quantidadeMedida: item.quantidade_medida,
          totalMedido: item.total_medido,
          detalhes: detalhesData || []
        };
      }));
      
      // Construir a obra fixa completa com os dados atualizados, convertendo snake_case para camelCase
      const obraFixaCompletaAtualizada = {
        id: obraFixaAtualizada.id,
        nome: obraFixaAtualizada.nome,
        empreiteiro: obraFixaAtualizada.empreiteiro,
        valor: obraFixaAtualizada.valor,
        status: obraFixaAtualizada.status,
        dataInicio: obraFixaAtualizada.data_inicio,
        dataPrevisaoTermino: obraFixaAtualizada.data_previsao_termino,
        observacoes: obraFixaAtualizada.observacoes,
        contrato: obraFixaAtualizada.contrato,
        licitacao: obraFixaAtualizada.licitacao,
        processo: obraFixaAtualizada.processo,
        gestor: obraFixaAtualizada.gestor,
        fiscaisObras: obraFixaAtualizada.fiscais_obras,
        totalMedicoes: obraFixaAtualizada.total_medicoes,
        localizacao: {
          latitude: obraFixaAtualizada.latitude,
          longitude: obraFixaAtualizada.longitude,
          nome: obraFixaAtualizada.nome_local,
          descricao: obraFixaAtualizada.descricao_local
        },
        itens: itensComDetalhes || []
      };
      
      // Atualizar o estado local
      setObrasFixas(prevObrasFixas => 
        prevObrasFixas.map(obraFixa => 
          obraFixa.id === parseInt(id) ? obraFixaCompletaAtualizada : obraFixa
        )
      );
      
      toast.success('Obra fixa atualizada com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao atualizar obra fixa:', error);
      toast.error('Erro ao atualizar obra fixa: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Excluir uma obra fixa
  const excluirObraFixa = async (id) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return false;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Excluir a obra fixa (as tabelas relacionadas devem ter ON DELETE CASCADE)
      const { error } = await supabase
        .from('obras_fixas')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Atualizar o estado local
      setObrasFixas(prevObrasFixas => prevObrasFixas.filter(obraFixa => obraFixa.id !== parseInt(id)));
      
      toast.success('Obra fixa excluída com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao excluir obra fixa:', error);
      toast.error('Erro ao excluir obra fixa: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Obter obra fixa por ID
  const obterObraFixaPorId = (id) => {
    return obrasFixas.find(obraFixa => obraFixa.id === parseInt(id));
  };

  // Filtrar obras fixas
  const filtrarObrasFixas = (filtros) => {
    return obrasFixas.filter(obraFixa => {
      const termoBusca = filtros.termo ? filtros.termo.toLowerCase() : '';
      const matchTermo = !termoBusca || 
        obraFixa.nome.toLowerCase().includes(termoBusca) || 
        obraFixa.empreiteiro.toLowerCase().includes(termoBusca);
      
      const matchStatus = !filtros.status || obraFixa.status === filtros.status;
      
      return matchTermo && matchStatus;
    });
  };

  const value = {
    obrasFixas,
    loading,
    error,
    adicionarObraFixa,
    atualizarObraFixa,
    excluirObraFixa,
    obterObraFixaPorId,
    filtrarObrasFixas
  };

  return (
    <ObrasFixasContext.Provider value={value}>
      {children}
    </ObrasFixasContext.Provider>
  );
};